/**
 * Iter 156 · Sales Command Center
 *
 * The intelligent overhaul of Sales & Invoices.
 *   • Bento-grid hero KPI ribbon
 *   • Live alerts feed (deal stalled, quote unsigned, overdue, payment received, concentration risk)
 *   • Reports library (Client concentration Pareto, DSO trend, Revenue by service, Sales rep leaderboard, Aging)
 *   • Theme-aware (Mercury/Linear/Stripe/Bold) via workspace settings
 *
 * Route: /app/sales-cc
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, AlertTriangle, Trophy, Wallet, Users, Sparkles,
  Bell, ArrowRight, RefreshCw, Zap, ChartPie, Clock, Award,
  Send, Mail, MessageCircle, Eye, FilePlus,
} from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar, Legend } from "recharts";
import { motion, AnimatePresence } from "framer-motion";

// Indian numbering formatter — Cr / L / K
function inr(n) {
  const v = Number(n || 0);
  const abs = Math.abs(v);
  if (abs >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
  if (abs >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
  if (abs >= 1e3) return `₹${(v / 1e3).toFixed(1)}K`;
  return `₹${v.toFixed(0)}`;
}
function inrExact(n) {
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Number(n || 0));
}

const THEMES = [
  { key: "default", label: "Default", accent: "#f97316", bg: "#ffffff", desc: "Acquilix native — punchy orange on off-white" },
  { key: "mercury", label: "Mercury Dark", accent: "#22c55e", bg: "#0a0a0f", desc: "Ramp-inspired dark mode with mono numbers" },
  { key: "linear", label: "Linear Soft", accent: "#111111", bg: "#fafaf7", desc: "Notion / Linear · soft neutrals, calm" },
  { key: "stripe", label: "Stripe Bright", accent: "#635bff", bg: "#f8fafc", desc: "Stripe fintech · indigo on ice-white" },
  { key: "bold", label: "Bold Neo", accent: "#facc15", bg: "#efeadf", desc: "Neo-brutalist yellow · sharp shadows" },
];

export default function SalesCommandCenter() {
  const { activeWorkspaceId } = useAuth();
  const nav = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [risk, setRisk] = useState(null);
  const [ticker, setTicker] = useState(null);
  const [nudges, setNudges] = useState(null);
  const [tab, setTab] = useState("overview");
  const [theme, setTheme] = useState("default");
  const [themeBusy, setThemeBusy] = useState(false);
  const [defaultLanding, setDefaultLanding] = useState("dashboard");

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    try {
      const [k, a, r, s, ty, nq] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/sales-cc/kpis`),
        api.get(`/workspaces/${activeWorkspaceId}/sales-cc/alerts`),
        api.get(`/workspaces/${activeWorkspaceId}/sales-cc/ar-at-risk`),
        api.get(`/workspaces/${activeWorkspaceId}/settings`),
        api.get(`/workspaces/${activeWorkspaceId}/sales-cc/since-yesterday`),
        api.get(`/workspaces/${activeWorkspaceId}/sales-cc/nudges/unopened-quotes?hours=72`),
      ]);
      setKpis(k.data);
      setAlerts(a.data);
      setRisk(r.data);
      setTicker(ty.data);
      setNudges(nq.data);
      const t = s.data?.settings?.finance_theme || "default";
      setTheme(t);
      setDefaultLanding(s.data?.settings?.default_landing || "dashboard");
    } catch { /* ignore */ }
  }, [activeWorkspaceId]);

  useEffect(() => { load(); }, [load]);

  const changeTheme = async (newTheme) => {
    setTheme(newTheme);
    setThemeBusy(true);
    try {
      await api.patch(`/workspaces/${activeWorkspaceId}/settings`, { finance_theme: newTheme });
      toast.success(`Theme: ${THEMES.find((t) => t.key === newTheme)?.label}`);
    } catch (e) { toast.error("Save failed"); }
    finally { setThemeBusy(false); }
  };

  const changeDefaultLanding = async (v) => {
    setDefaultLanding(v);
    try {
      await api.patch(`/workspaces/${activeWorkspaceId}/settings`, { default_landing: v });
      toast.success(v === "sales-cc" ? "Sales Command is now your default landing 🎯" : "Landing page updated");
    } catch { toast.error("Save failed"); }
  };

  const themeClass = theme === "default" ? "" : `theme-${theme}`;

  return (
    <div className={`sales-cc ${themeClass} min-h-screen`} data-testid="sales-command-center">
      {/* Top bar with theme picker */}
      <div className="border-b border-border/40 px-6 py-3 flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Zap className="h-5 w-5" style={{ color: "hsl(var(--primary))" }} />
          <h1 className="text-xl font-black tracking-tight">Sales Command Center</h1>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Look</span>
          {THEMES.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => changeTheme(t.key)}
              disabled={themeBusy}
              className={`h-8 rounded-md border-2 px-3 text-xs transition-all ${
                theme === t.key ? "border-primary ring-2 ring-primary/40" : "border-border/40 hover:border-primary/60"
              }`}
              title={t.desc}
              data-testid={`theme-btn-${t.key}`}
              style={{ background: t.bg, color: t.accent, fontWeight: 700 }}
            >
              {t.label}
            </button>
          ))}
          <Button size="sm" variant="ghost" onClick={load} data-testid="sales-cc-refresh">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
          <label className="ml-2 flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground cursor-pointer" title="Land here every time you open the app">
            <input type="checkbox"
                   checked={defaultLanding === "sales-cc"}
                   onChange={(e) => changeDefaultLanding(e.target.checked ? "sales-cc" : "dashboard")}
                   data-testid="cc-set-default-landing" />
            Set as home
          </label>
        </div>
      </div>

      <div className="p-6 md:p-8 space-y-8">
        {/* What changed since yesterday · ticker (Iter 160) */}
        {ticker && ticker.items && ticker.items.length > 0 && (
          <ChangedTicker items={ticker.items} generatedAt={ticker.generated_at} />
        )}

        {/* Bento KPI Hero */}
        {kpis && <BentoHero kpis={kpis} risk={risk} />}

        {/* Unopened Quote Nudges strip (Iter 160) */}
        {nudges && nudges.rows && nudges.rows.length > 0 && (
          <UnopenedQuotesStrip rows={nudges.rows} onDone={load} />
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-lg">
            <TabsTrigger value="overview" data-testid="cc-tab-overview">Overview & Alerts</TabsTrigger>
            <TabsTrigger value="reports" data-testid="cc-tab-reports">Reports</TabsTrigger>
            <TabsTrigger value="risk" data-testid="cc-tab-risk">AR at Risk</TabsTrigger>
            <TabsTrigger value="theme" data-testid="cc-tab-theme">Theme gallery</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="pt-6">
            {alerts && <AlertsFeed alerts={alerts.alerts} nav={nav} />}
          </TabsContent>
          <TabsContent value="reports" className="pt-6">
            <ReportsLibrary wsId={activeWorkspaceId} />
          </TabsContent>
          <TabsContent value="risk" className="pt-6">
            {risk && <ArRiskTable risk={risk} nav={nav} />}
          </TabsContent>
          <TabsContent value="theme" className="pt-6">
            <ThemeGallery current={theme} onPick={changeTheme} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ─────────── Bento Hero ───────────

function BentoHero({ kpis, risk }) {
  const container = { hidden: {}, show: { transition: { staggerChildren: 0.06 } } };
  const item = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } } };
  const M = motion.div;
  return (
    <motion.div initial="hidden" animate="show" variants={container} className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="bento-hero">
      {/* Won this week — hero (2x2) */}
      <M
        variants={item}
        whileHover={{ scale: 1.01 }}
        data-brutal-card
        className="md:col-span-2 md:row-span-2 rounded-xl border border-border bg-card p-6 flex flex-col justify-between overflow-hidden relative"
        style={{ minHeight: 220 }}
      >
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
          <Trophy className="h-3.5 w-3.5" style={{ color: "hsl(var(--primary))" }} /> Won this week
        </div>
        <div>
          <div className="font-mono text-4xl md:text-6xl font-black leading-none">
            {inr(kpis.won_this_week.amount)}
          </div>
          <div className="mt-2 text-xs text-muted-foreground">
            {kpis.won_this_week.count} deal{kpis.won_this_week.count !== 1 ? "s" : ""} accepted · 30d velocity {inr(kpis.pipeline_velocity_30d)}
          </div>
        </div>
        <div className="absolute right-0 bottom-0 opacity-[0.04] text-9xl font-black leading-none pr-4">₹</div>
      </M>

      {/* Overdue */}
      <motion.div variants={item} whileHover={{ y: -2 }}>
        <HoverKpi
          title="Overdue"
          icon={<AlertTriangle className="h-3.5 w-3.5 text-red-500" />}
          value={inr(kpis.overdue.amount)}
          subtitle={`${kpis.overdue.count} invoices`}
          formula="Sum of balance_due on invoices where status ∈ {sent, partial, overdue} and due_date < today."
          drillTo="/app/finance/revenue-flow?tab=waterfall&drill=invoiced"
          drillLabel="See underlying invoices →"
          testid="bento-overdue"
        />
      </motion.div>

      {/* Cash today */}
      <motion.div variants={item} whileHover={{ y: -2 }}>
        <HoverKpi
          title="Collected today"
          icon={<Wallet className="h-3.5 w-3.5 text-emerald-500" />}
          value={inr(kpis.cash_collected_today.amount)}
          subtitle={`${kpis.cash_collected_today.count} payment${kpis.cash_collected_today.count !== 1 ? "s" : ""}`}
          formula="Sum of amount from payments where payment_date = today (or paid_at ≥ today 00:00 UTC)."
          drillTo="/app/sales?tab=payments"
          drillLabel="See payments →"
          testid="bento-cash-today"
        />
      </motion.div>

      {/* Top customer */}
      <motion.div variants={item} whileHover={{ y: -2 }}>
        <HoverKpi
          title="Top customer · 90d"
          icon={<Users className="h-3.5 w-3.5" style={{ color: "hsl(var(--primary))" }} />}
          value={kpis.top_customer_90d?.name || "—"}
          subtitle={inr(kpis.top_customer_90d?.revenue_90d || 0)}
          formula="Rolling 90-day revenue per client (sum of grand_total on invoices where issue_date ≥ T-90d). Highest wins."
          drillTo={kpis.top_customer_90d ? `/app/clients?client_id=${kpis.top_customer_90d.client_id}` : null}
          drillLabel="View client →"
          testid="bento-top-customer"
        />
      </motion.div>

      {/* DSO */}
      <motion.div variants={item} whileHover={{ y: -2 }}>
        <HoverKpi
          title="DSO"
          icon={<Clock className="h-3.5 w-3.5 text-amber-500" />}
          value={`${kpis.dso_days}d`}
          subtitle={`AR ${inr(kpis.ar_outstanding)}`}
          formula={`Days Sales Outstanding = AR outstanding (₹${(kpis.ar_outstanding || 0).toLocaleString("en-IN")}) ÷ average daily revenue over last 90 days. Lower is better; benchmark <45d.`}
          drillTo="/app/finance/revenue-flow"
          drillLabel="Explore →"
          testid="bento-dso"
        />
      </motion.div>

      {/* AR at Risk (spotlight) */}
      {risk && (
        <motion.div
          variants={item}
          whileHover={{ y: -2 }}
          data-brutal-card
          className="md:col-span-2 rounded-xl border-2 p-5 relative overflow-hidden"
          style={{ borderColor: "hsl(var(--accent))", background: "hsl(var(--card))" }}
          data-testid="bento-ar-risk"
        >
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
            <AlertTriangle className="h-3.5 w-3.5 text-red-500" /> AR at Risk this week
          </div>
          <div className="font-mono text-3xl font-bold mt-2">{inr(risk.total_at_risk)}</div>
          <div className="text-xs text-muted-foreground">{risk.invoice_count} at-risk invoices · Top 5 to chase:</div>
          <div className="mt-2 space-y-1">
            {(risk.top_risks || []).slice(0, 5).map((r) => (
              <div key={r.invoice_id} className="flex items-center gap-2 text-xs">
                <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                <div className="flex-1 truncate font-medium">{r.client_name}</div>
                <div className="font-mono text-muted-foreground">{r.days_overdue}d</div>
                <div className="font-mono font-semibold">{inr(r.risk_amount)}</div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}

// ─────────── Alerts Feed ───────────

function AlertsFeed({ alerts, nav }) {
  const iconFor = (kind) => {
    if (kind === "payment_received") return <Award className="h-4 w-4 text-emerald-600" />;
    if (kind === "invoice_overdue") return <AlertTriangle className="h-4 w-4 text-red-500" />;
    if (kind === "quote_unsigned") return <Clock className="h-4 w-4 text-amber-500" />;
    if (kind === "concentration_risk") return <TrendingDown className="h-4 w-4 text-amber-500" />;
    return <Bell className="h-4 w-4" />;
  };
  const borderFor = (sev) =>
    sev === "red" ? "border-red-500"
    : sev === "amber" ? "border-amber-500"
    : sev === "green" ? "border-emerald-500"
    : "border-transparent";

  if (!alerts || alerts.length === 0) {
    return (
      <div data-brutal-card className="rounded-xl border border-border bg-card p-8 text-center" data-testid="alerts-empty">
        <Sparkles className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
        <div className="text-sm text-muted-foreground">All clear — no alerts.</div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="alerts-feed">
      {alerts.map((a, i) => (
        <button
          key={i}
          type="button"
          onClick={() => a.link && nav(a.link)}
          className={`text-left rounded-lg border border-border bg-card p-4 border-l-4 ${borderFor(a.severity)} hover:bg-muted/30 transition-colors`}
          data-testid={`alert-${i}`}
        >
          <div className="flex items-start gap-3">
            <div className="mt-0.5">{iconFor(a.kind)}</div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">{a.title}</div>
              <div className="text-xs text-muted-foreground truncate">{a.detail}</div>
            </div>
            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground mt-1" />
          </div>
        </button>
      ))}
    </div>
  );
}

// ─────────── AR Risk Table ───────────

function ArRiskTable({ risk, nav }) {
  return (
    <div data-brutal-card className="rounded-xl border border-border bg-card overflow-hidden" data-testid="risk-table">
      <div className="border-b border-border p-4">
        <div className="text-sm font-semibold">
          <span className="text-red-500">{inr(risk.total_at_risk)}</span> at risk across {risk.invoice_count} invoices
        </div>
        <div className="text-xs text-muted-foreground">Weighted by aging bucket + client&apos;s historic days-to-pay.</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left p-3">Invoice</th>
              <th className="text-left p-3">Client</th>
              <th className="text-right p-3">Balance</th>
              <th className="text-right p-3">Overdue</th>
              <th className="text-right p-3">Client avg pay</th>
              <th className="text-right p-3">Weight</th>
              <th className="text-right p-3">Risk ₹</th>
            </tr>
          </thead>
          <tbody>
            {(risk.all_risks || []).slice(0, 25).map((r) => (
              <tr key={r.invoice_id} className="border-t border-border hover:bg-muted/20 cursor-pointer"
                  onClick={() => nav(`/app/sales?tab=invoices&open=${r.invoice_id}`)}
                  data-testid={`risk-row-${r.invoice_id}`}>
                <td className="p-3 font-mono text-xs">{r.doc_no}</td>
                <td className="p-3">{r.client_name}</td>
                <td className="p-3 text-right font-mono">{inr(r.balance_due)}</td>
                <td className="p-3 text-right">
                  <span className={r.days_overdue > 60 ? "text-red-500 font-bold" : r.days_overdue > 30 ? "text-amber-500 font-semibold" : ""}>
                    {r.days_overdue}d
                  </span>
                </td>
                <td className="p-3 text-right text-xs text-muted-foreground">{r.client_avg_days_to_pay != null ? `${r.client_avg_days_to_pay}d` : "—"}</td>
                <td className="p-3 text-right text-xs">{(r.risk_weight * 100).toFixed(0)}%</td>
                <td className="p-3 text-right font-mono font-semibold text-red-500">{inr(r.risk_amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────── Reports library ───────────

function ReportsLibrary({ wsId }) {
  const [reports, setReports] = useState({});
  const [repDrill, setRepDrill] = useState(null);
  const [repData, setRepData] = useState(null);
  useEffect(() => {
    if (!repDrill || !wsId) { setRepData(null); return; }
    api.get(`/workspaces/${wsId}/sales-cc/reports/sales-rep/${repDrill}/activity`)
      .then(({ data }) => setRepData(data))
      .catch(() => setRepData(null));
  }, [repDrill, wsId]);
  useEffect(() => {
    if (!wsId) return;
    (async () => {
      try {
        const [cc, dso, rbs, srl, ag] = await Promise.all([
          api.get(`/workspaces/${wsId}/sales-cc/reports/client_concentration`),
          api.get(`/workspaces/${wsId}/sales-cc/reports/dso_trend`),
          api.get(`/workspaces/${wsId}/sales-cc/reports/revenue_by_service`),
          api.get(`/workspaces/${wsId}/sales-cc/reports/sales_rep_leaderboard`),
          api.get(`/workspaces/${wsId}/sales-cc/reports/aging_trend`),
        ]);
        setReports({ cc: cc.data, dso: dso.data, rbs: rbs.data, srl: srl.data, ag: ag.data });
      } catch { /* ignore */ }
    })();
  }, [wsId]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="reports-library">
      {/* Client concentration (Pareto) */}
      <div data-brutal-card className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <ChartPie className="h-4 w-4" style={{ color: "hsl(var(--primary))" }} />
          <h3 className="font-bold">Client concentration (Pareto)</h3>
        </div>
        {reports.cc && (
          <>
            <div className="text-xs text-muted-foreground mb-3">Top 5 = <b>{reports.cc.gini_top5_pct}%</b> of last-12mo revenue ({inr(reports.cc.total)}).</div>
            <div className="space-y-2">
              {(reports.cc.rows || []).slice(0, 8).map((r) => (
                <div key={r.client_id} className="flex items-center gap-2 text-xs">
                  <div className="w-28 truncate font-medium">{r.client_name}</div>
                  <div className="flex-1 h-4 bg-muted/40 relative">
                    <div className="h-4 bg-primary/70" style={{ width: `${Math.min(100, r.share_pct * 2)}%` }} />
                  </div>
                  <div className="font-mono w-14 text-right">{r.share_pct}%</div>
                  <div className="font-mono w-16 text-right text-muted-foreground">{inr(r.revenue)}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* DSO trend */}
      <div data-brutal-card className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="h-4 w-4 text-emerald-500" />
          <h3 className="font-bold">DSO trend · 12 months</h3>
        </div>
        {reports.dso && (
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={reports.dso.rows}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.35} />
              <XAxis dataKey="period" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => `${v}d`} />
              <Line type="monotone" dataKey="dso" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Revenue by service */}
      <div data-brutal-card className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-4 w-4" style={{ color: "hsl(var(--primary))" }} />
          <h3 className="font-bold">Revenue by service · 90d</h3>
        </div>
        {reports.rbs && (
          <div className="space-y-1.5">
            {(reports.rbs.rows || []).slice(0, 8).map((r, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <div className="flex-1 truncate">{r.service}</div>
                <div className="font-mono w-16 text-right">{inr(r.revenue)}</div>
                <div className="text-muted-foreground w-10 text-right">{r.count}×</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sales rep leaderboard */}
      <div data-brutal-card className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <Award className="h-4 w-4" style={{ color: "hsl(var(--primary))" }} />
          <h3 className="font-bold">Sales rep leaderboard · 90d</h3>
          <span className="text-[10px] text-muted-foreground ml-auto">click a rep for activity feed</span>
        </div>
        {reports.srl && (
          <div className="space-y-1.5">
            {(reports.srl.rows || []).slice(0, 8).map((r, i) => (
              <button
                key={r.user_id}
                type="button"
                onClick={() => r.user_id !== "unassigned" && setRepDrill(r.user_id)}
                disabled={r.user_id === "unassigned"}
                className={`flex items-center gap-3 text-xs w-full text-left rounded-md px-2 py-1 ${r.user_id !== "unassigned" ? "hover:bg-muted/40 cursor-pointer" : "cursor-default"}`}
                data-testid={`rep-row-${r.user_id}`}
              >
                <div className={`w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs ${i === 0 ? "bg-amber-400 text-black" : i === 1 ? "bg-gray-300 text-black" : i === 2 ? "bg-amber-700 text-white" : "bg-muted"}`}>{i + 1}</div>
                <div className="flex-1 font-medium truncate">{r.name}</div>
                <div className="font-mono">{inr(r.revenue)}</div>
                <div className="text-muted-foreground w-10 text-right">{r.count}×</div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Aging trend */}
      <div data-brutal-card className="rounded-xl border border-border bg-card p-5 lg:col-span-2">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="h-4 w-4 text-amber-500" />
          <h3 className="font-bold">Aged receivables (current snapshot)</h3>
        </div>
        {reports.ag && (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={[
              { bucket: "Current", amount: reports.ag.buckets.current },
              { bucket: "0-30d", amount: reports.ag.buckets["0_30"] },
              { bucket: "31-60d", amount: reports.ag.buckets["31_60"] },
              { bucket: "61-90d", amount: reports.ag.buckets["61_90"] },
              { bucket: "90+ d", amount: reports.ag.buckets["90_plus"] },
            ]}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.35} />
              <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => inr(v)} />
              <Tooltip formatter={(v) => `₹${inrExact(v)}`} />
              <Legend />
              <Bar dataKey="amount" name="Balance" fill="hsl(var(--primary))" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Rep activity drill-down slide-over */}
      <AnimatePresence>
        {repDrill && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex"
            onClick={() => setRepDrill(null)}
            data-testid="rep-drill"
          >
            <div className="flex-1 bg-black/40" />
            <motion.div
              initial={{ x: 500 }} animate={{ x: 0 }} exit={{ x: 500 }}
              transition={{ type: "spring", damping: 32, stiffness: 260 }}
              className="w-full max-w-3xl bg-white h-full overflow-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-border p-4 flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Rep activity · 90d</div>
                  <div className="text-lg font-bold">{repData?.rep_name || "Loading…"}</div>
                </div>
                <button onClick={() => setRepDrill(null)} className="rounded-none border border-border px-3 py-1 text-xs hover:bg-muted" data-testid="rep-drill-close">Close</button>
              </div>
              {repData && (
                <div className="p-4 space-y-4">
                  <div className="grid grid-cols-4 gap-2">
                    <MiniKpi label="Quoted" value={inr(repData.totals.quoted)} />
                    <MiniKpi label="Won" value={inr(repData.totals.won)} tint="text-emerald-700" />
                    <MiniKpi label="Invoiced" value={inr(repData.totals.invoiced)} />
                    <MiniKpi label="Win rate" value={`${repData.totals.win_rate_pct}%`} tint="text-blue-700" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 font-bold">Quotations ({repData.quotations.length})</div>
                    <RepList rows={repData.quotations} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 font-bold">Invoices ({repData.invoices.length})</div>
                    <RepList rows={repData.invoices} />
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function MiniKpi({ label, value, tint }) {
  return (
    <div className="rounded-md border border-border p-2">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`font-mono font-bold text-sm ${tint || ""}`}>{value}</div>
    </div>
  );
}

function RepList({ rows }) {
  const inrLocal = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Number(n || 0));
  return (
    <div className="rounded-md border border-border divide-y divide-border text-xs">
      {rows.length === 0 && <div className="p-4 text-center text-muted-foreground">Nothing in the last 90 days</div>}
      {rows.slice(0, 20).map((r) => (
        <div key={r.id} className="p-2 flex items-center gap-2" data-testid={`rep-row-${r.id}`}>
          <span className="font-mono">{r.doc_no}</span>
          <span className="flex-1 truncate">{r.client_name}</span>
          <span className="text-muted-foreground">{r.issue_date}</span>
          <span className="font-mono font-semibold">₹{inrLocal(r.grand_total)}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase font-bold ${r.status === "paid" || r.status === "accepted" ? "bg-emerald-100 text-emerald-800" : r.status === "overdue" || r.status === "rejected" ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-700"}`}>{r.status}</span>
        </div>
      ))}
    </div>
  );
}

// ─────────── Theme gallery ───────────

function ThemeGallery({ current, onPick }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="theme-gallery">
      {THEMES.map((t) => (
        <button
          key={t.key}
          type="button"
          onClick={() => onPick(t.key)}
          className={`text-left rounded-xl border-2 overflow-hidden transition-all ${current === t.key ? "border-primary ring-4 ring-primary/20" : "border-border/40 hover:border-primary/60"}`}
          data-testid={`theme-gallery-${t.key}`}
        >
          <div className="h-24 flex items-center px-6" style={{ background: t.bg }}>
            <div className="text-2xl font-black" style={{ color: t.accent }}>{t.label}</div>
          </div>
          <div className="p-4 bg-card">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-4 h-4 rounded" style={{ background: t.accent }} />
              <div className="text-xs font-mono text-muted-foreground">{t.accent}</div>
            </div>
            <div className="text-xs text-muted-foreground">{t.desc}</div>
            {current === t.key && (
              <div className="mt-2 text-xs font-bold" style={{ color: "hsl(var(--primary))" }}>● Active</div>
            )}
          </div>
        </button>
      ))}
    </div>
  );
}

// ─────────── HoverKpi — KPI tile with explainability Popover ───────────

function HoverKpi({ title, icon, value, subtitle, formula, drillTo, drillLabel, testid }) {
  const nav = useNavigate();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-brutal-card
          className="text-left rounded-xl border border-border bg-card p-5 hover:border-primary/60 transition-colors group w-full"
          data-testid={testid}
        >
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
            {icon} {title}
            <Sparkles className="h-3 w-3 opacity-0 group-hover:opacity-60 ml-auto" />
          </div>
          <div className={`mt-2 ${typeof value === "string" && value.length > 6 ? "text-lg font-bold truncate" : "font-mono text-3xl font-bold"}`}>
            {value}
          </div>
          {subtitle && <div className="text-xs text-muted-foreground mt-1 font-mono">{subtitle}</div>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80" data-testid={`${testid}-hover`}>
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1 font-bold">{title}</div>
        <div className="text-lg font-mono font-bold mb-2">{value}</div>
        <div className="text-xs text-muted-foreground leading-relaxed border-t border-border pt-2">
          <div className="mb-1 uppercase tracking-widest text-[10px] font-bold">How this is calculated</div>
          <div>{formula}</div>
        </div>
        {drillTo && (
          <button
            onClick={() => nav(drillTo)}
            className="mt-3 text-xs font-semibold flex items-center gap-1"
            style={{ color: "hsl(var(--primary))" }}
            data-testid={`${testid}-drill`}
          >
            {drillLabel} <ArrowRight className="h-3 w-3" />
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}



// ─────────── ChangedTicker · "What changed since yesterday" ───────────

function ChangedTicker({ items, generatedAt }) {
  const iconFor = (kind) => {
    if (kind === "quotes_accepted") return <Trophy className="h-3.5 w-3.5 text-emerald-500" />;
    if (kind === "cash_collected") return <Wallet className="h-3.5 w-3.5 text-emerald-500" />;
    if (kind === "invoices_raised") return <FilePlus className="h-3.5 w-3.5 text-blue-500" />;
    if (kind === "tipped_overdue") return <AlertTriangle className="h-3.5 w-3.5 text-red-500" />;
    if (kind === "doc_opened") return <Eye className="h-3.5 w-3.5 text-blue-500" />;
    return <Sparkles className="h-3.5 w-3.5" />;
  };
  const toneClass = (tone) => (
    tone === "green" ? "text-emerald-700"
      : tone === "red" ? "text-red-600"
      : tone === "blue" ? "text-blue-700"
      : "text-foreground"
  );
  // Duplicate items so the marquee loops seamlessly.
  const track = [...items, ...items];
  const ts = generatedAt ? new Date(generatedAt) : null;
  return (
    <div
      data-testid="since-yesterday-ticker"
      className="relative overflow-hidden rounded-xl border border-border bg-card"
      style={{ borderLeftWidth: 4, borderLeftColor: "hsl(var(--primary))" }}
    >
      <div className="flex items-center gap-3 px-4 py-2.5">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground shrink-0">
          <Sparkles className="h-3 w-3" style={{ color: "hsl(var(--primary))" }} />
          Since yesterday
        </div>
        <div className="flex-1 overflow-hidden relative">
          <motion.div
            className="flex items-center gap-6 whitespace-nowrap"
            animate={{ x: ["0%", "-50%"] }}
            transition={{ duration: Math.max(20, track.length * 3.5),
                          repeat: Infinity, ease: "linear" }}
            data-testid="ticker-track"
          >
            {track.map((it, i) => (
              <div
                key={i}
                className={`flex items-center gap-1.5 text-sm font-medium ${toneClass(it.tone)}`}
                data-testid={`ticker-item-${i % items.length}`}
              >
                {iconFor(it.kind)}
                <span>{it.text}</span>
                <span className="text-muted-foreground/50 ml-2">·</span>
              </div>
            ))}
          </motion.div>
        </div>
        {ts && (
          <div className="text-[10px] text-muted-foreground shrink-0 font-mono" title={ts.toLocaleString()}>
            {ts.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────── UnopenedQuotesStrip · one-click nudges ───────────

function UnopenedQuotesStrip({ rows, onDone }) {
  const [busy, setBusy] = useState({});
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? rows : rows.slice(0, 4);
  const inrL = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Number(n || 0));

  const nudge = async (row, channel) => {
    setBusy((b) => ({ ...b, [row.quote_id]: channel }));
    try {
      const { data } = await api.post(`/sales-cc/quote/${row.quote_id}/nudge`, {
        channel,
        to_emails: row.client_email ? [row.client_email] : [],
        whatsapp_to: row.client_phone || null,
        subject: row.subject, body: row.body, whatsapp: row.whatsapp,
      });
      const overall = data?.delivery?.overall;
      if (overall === "sent") {
        toast.success(`Nudge sent to ${row.client_name}`);
      } else if (overall === "failed") {
        toast.error(`Nudge failed — check integration settings`);
      } else {
        toast.success(`Nudge tracked (provider not configured — mocked send)`);
      }
      if (onDone) onDone();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to send nudge";
      toast.error(msg);
    } finally {
      setBusy((b) => { const n = { ...b }; delete n[row.quote_id]; return n; });
    }
  };

  return (
    <div data-testid="unopened-quotes-strip" data-brutal-card
         className="rounded-xl border-2 border-amber-300 bg-amber-50/40 dark:bg-amber-950/10 p-5">
      <div className="flex items-center gap-2 mb-3">
        <Clock className="h-4 w-4 text-amber-600" />
        <h3 className="font-bold text-sm">
          Unopened quotes · <span className="text-amber-700">{rows.length}</span> waiting for a nudge
        </h3>
        <span className="ml-auto text-[10px] uppercase tracking-widest text-muted-foreground">
          Sent 72h+ ago · 0 opens
        </span>
      </div>
      <div className="space-y-2">
        {visible.map((r) => {
          const bz = busy[r.quote_id];
          return (
            <div key={r.quote_id}
                 className="flex items-center gap-3 rounded-lg border border-border bg-card p-3"
                 data-testid={`nudge-row-${r.quote_id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-mono font-semibold text-xs bg-muted px-1.5 py-0.5 rounded">
                    {r.doc_no}
                  </span>
                  <span className="font-semibold truncate">{r.client_name}</span>
                  <span className="font-mono text-xs text-muted-foreground">
                    ₹{inrL(r.grand_total)}
                  </span>
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  Sent {r.days_since_sent}d ago
                  {r.client_email && <> · <Mail className="inline h-3 w-3 mr-0.5" />{r.client_email}</>}
                  {r.client_phone && <> · <MessageCircle className="inline h-3 w-3 mr-0.5" />{r.client_phone}</>}
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Button
                  size="sm" variant="outline"
                  disabled={!r.client_email || !!bz}
                  onClick={() => nudge(r, "email")}
                  data-testid={`nudge-btn-email-${r.quote_id}`}
                  title={r.client_email ? "Send email nudge" : "No email on client record"}
                >
                  {bz === "email" ? "…" : <><Send className="h-3 w-3 mr-1" /> Email</>}
                </Button>
                {r.client_phone && (
                  <Button
                    size="sm" variant="outline"
                    disabled={!!bz}
                    onClick={() => nudge(r, "whatsapp")}
                    data-testid={`nudge-btn-whatsapp-${r.quote_id}`}
                    title="Send WhatsApp nudge"
                  >
                    {bz === "whatsapp" ? "…" : <><MessageCircle className="h-3 w-3 mr-1" /> WA</>}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {rows.length > 4 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-3 text-xs font-semibold hover:underline"
          style={{ color: "hsl(var(--primary))" }}
          data-testid="nudges-toggle-expand"
        >
          {expanded ? `Show top 4 only` : `Show all ${rows.length} unopened quotes →`}
        </button>
      )}
    </div>
  );
}
